import * as i0 from '@angular/core';
import { EventEmitter, Directive, HostBinding, Output, Component, Input, HostListener, ElementRef, ViewChild, ViewChildren, ContentChild, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$1 from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import * as i3 from '@aihub/ui/dropdown';
import { OverlayWidthStrategy, AihubDropdownTriggerDirective, AihubDropdownModule } from '@aihub/ui/dropdown';
import * as i1 from '@angular/cdk/a11y';
import { FocusKeyManager } from '@angular/cdk/a11y';

class OptionDirective {
    el;
    focusMonitor;
    renderer;
    tabindex = '-1';
    cssClass = 'listitem';
    monitor;
    constructor(el, focusMonitor, renderer) {
        this.el = el;
        this.focusMonitor = focusMonitor;
        this.renderer = renderer;
    }
    focusChange = new EventEmitter();
    focus() {
        this.el.nativeElement.focus();
    }
    ngOnDestroy() {
        this.monitor.unsubscribe();
    }
    ngOnInit() {
        this.monitor = this.focusMonitor.monitor(this.el.nativeElement, true)
            .subscribe((state) => {
            state !== null
                ? this.renderer.addClass(this.el.nativeElement, 'focus')
                : this.renderer.removeClass(this.el.nativeElement, 'focus');
            this.focusChange.emit(state !== null);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionDirective, deps: [{ token: i0.ElementRef }, { token: i1.FocusMonitor }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: OptionDirective, selector: "[aihubSelectOption]", outputs: { focusChange: "focusChange" }, host: { properties: { "tabindex": "this.tabindex", "class": "this.cssClass" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aihubSelectOption]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.FocusMonitor }, { type: i0.Renderer2 }]; }, propDecorators: { tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], cssClass: [{
                type: HostBinding,
                args: ['class']
            }], focusChange: [{
                type: Output
            }] } });
class OptionComponent {
    disabled = false;
    selectItem;
    data;
    getLabel() {
        return this.data.label;
    }
    constructor() {
        this.selectItem = new EventEmitter();
    }
    keyDown(event) {
        if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            this.selectItem.emit(this.data);
        }
    }
    click($event) {
        $event.stopPropagation();
        $event.preventDefault();
        this.selectItem.emit(this.data);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: OptionComponent, selector: "aihub-select-option", inputs: { disabled: "disabled", data: "data" }, outputs: { selectItem: "selectItem" }, host: { listeners: { "keydown": "keyDown($event)", "click": "click($event)" } }, ngImport: i0, template: "{{data.label}}\n", styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-select-option', template: "{{data.label}}\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { disabled: [{
                type: Input
            }], selectItem: [{
                type: Output
            }], data: [{
                type: Input
            }], keyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], click: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class OptionListComponent {
    formBuilder;
    overlayPosition;
    overlayWidth = OverlayWidthStrategy.HOST;
    freetext = false;
    /**
     * field label
     *
     */
    label;
    /**
     * available options
     *
     */
    options = [];
    /**
     * selected value
     *
     */
    selected = '';
    /**
     * selected value
     *
     */
    maxlength;
    /**
     * event emitter we selected an item
     *
     */
    selectItem = new EventEmitter();
    /**
     * our form control
     *
     */
    textfield;
    /**
     * the elementref for our html element, we need this to know
     * if the focus changed on the Option we have to focus the input
     * field or blur it
     *
     */
    textfieldEl;
    /**
     * all items we have to add to the keymanager
     *
     */
    items;
    /**
     *
     *
     */
    keyManager;
    /**
     *
     *
     */
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
    }
    /**
     * listen to keyboard events and pass through to keymanager
     *
     */
    changeFocus($event) {
        if ($event.key === 'Enter') {
            return;
        }
        this.keyManager.onKeydown($event);
    }
    /**
     * if this will open up activate the correct item
     *
     */
    enable() {
        const itemIndex = this.options.findIndex((option) => option.value === this.selected);
        if (itemIndex > -1) {
            this.keyManager.setActiveItem(itemIndex);
            return;
        }
        if (this.selected === '' || !this.freetext) {
            this.keyManager.setFirstItemActive();
            return;
        }
        this.textfield.setValue(this.selected);
        this.textfieldEl.nativeElement.focus();
    }
    /**
     * text field is a bit more special since this is wrapped into a div container
     * so if the div container is focused we will move our focus to the textfield directly
     * so we can start typeing
     *
     */
    toggleFocusTextfield(focused) {
        focused
            ? this.textfieldEl.nativeElement.focus()
            : this.textfieldEl.nativeElement.blur();
    }
    /**
     *
     *
     */
    ngOnInit() {
        this.overlayPosition = [{
                originY: 'top',
                originX: 'start',
                offsetY: 10,
                overlayY: 'top',
                overlayX: 'start'
            }];
        this.textfield = this.formBuilder.control('');
    }
    /**
     *
     *
     */
    ngAfterViewInit() {
        this.keyManager = new FocusKeyManager(this.items).withWrap();
    }
    /**
     *
     *
     */
    selectOption(option) {
        const selected = option instanceof FormControl
            ? { label: option.value, value: option.value }
            : option;
        this.textfield.markAsTouched();
        this.selectItem.emit(selected);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionListComponent, deps: [{ token: i1$1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: OptionListComponent, selector: "aihub-select-option-list", inputs: { freetext: "freetext", label: "label", options: "options", selected: "selected", maxlength: "maxlength" }, outputs: { selectItem: "selectItem" }, host: { listeners: { "keydown": "changeFocus($event)" } }, viewQueries: [{ propertyName: "textfieldEl", first: true, predicate: ["textfieldEl"], descendants: true, read: ElementRef }, { propertyName: "items", predicate: OptionDirective, descendants: true, read: OptionDirective }], ngImport: i0, template: "<aihub-select-option aihubSelectOption *ngFor=\"let option of options\" [data]=\"option\" (selectItem)=\"selectOption($event)\">\n</aihub-select-option>\n\n<div class=\"aihub-select--textfield\" aihubSelectOption (focusChange)=\"toggleFocusTextfield($event)\" *ngIf=\"freetext\">\n  <label>\n    <span>Other</span>\n    <input type=\"text\" #textfieldEl [formControl]=\"textfield\" (keyup.enter)=\"selectOption(textfield)\" autocomplete=\"none\" [attr.maxlength]=\"maxlength || null\">\n    <small>Type your answer and press Enter to save.</small>\n  </label>\n</div>\n", styles: [":host{background:white;border:1px solid #e9e9e9;border-radius:2px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;display:flex;flex-direction:column;font-family:OpenSansRegular;font-size:14px;padding:20px 19px 20px 0}:host .listitem{margin-bottom:10px;border-left:4px solid transparent;padding-left:15px;line-height:34px;font-size:14px;cursor:pointer}:host .listitem.focus,:host .listitem:focus{border-color:#86bc25}:host .listitem.focus:not(.ai-hub-form--control),:host .listitem:focus:not(.ai-hub-form--control){color:#86bc25;font-family:OpenSansBold}:host .aihub-select--textfield label{display:block}:host .aihub-select--textfield input[type=text]{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;line-height:24px;font-family:OpenSansRegular}:host .aihub-select--textfield input[type=text]:focus{outline:none;border:1px solid #86bc25;box-shadow:0 5px 5px #86bc2552}:host .aihub-select--textfield small{color:#000}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: OptionComponent, selector: "aihub-select-option", inputs: ["disabled", "data"], outputs: ["selectItem"] }, { kind: "directive", type: OptionDirective, selector: "[aihubSelectOption]", outputs: ["focusChange"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OptionListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-select-option-list', template: "<aihub-select-option aihubSelectOption *ngFor=\"let option of options\" [data]=\"option\" (selectItem)=\"selectOption($event)\">\n</aihub-select-option>\n\n<div class=\"aihub-select--textfield\" aihubSelectOption (focusChange)=\"toggleFocusTextfield($event)\" *ngIf=\"freetext\">\n  <label>\n    <span>Other</span>\n    <input type=\"text\" #textfieldEl [formControl]=\"textfield\" (keyup.enter)=\"selectOption(textfield)\" autocomplete=\"none\" [attr.maxlength]=\"maxlength || null\">\n    <small>Type your answer and press Enter to save.</small>\n  </label>\n</div>\n", styles: [":host{background:white;border:1px solid #e9e9e9;border-radius:2px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;display:flex;flex-direction:column;font-family:OpenSansRegular;font-size:14px;padding:20px 19px 20px 0}:host .listitem{margin-bottom:10px;border-left:4px solid transparent;padding-left:15px;line-height:34px;font-size:14px;cursor:pointer}:host .listitem.focus,:host .listitem:focus{border-color:#86bc25}:host .listitem.focus:not(.ai-hub-form--control),:host .listitem:focus:not(.ai-hub-form--control){color:#86bc25;font-family:OpenSansBold}:host .aihub-select--textfield label{display:block}:host .aihub-select--textfield input[type=text]{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;line-height:24px;font-family:OpenSansRegular}:host .aihub-select--textfield input[type=text]:focus{outline:none;border:1px solid #86bc25;box-shadow:0 5px 5px #86bc2552}:host .aihub-select--textfield small{color:#000}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }]; }, propDecorators: { freetext: [{
                type: Input
            }], label: [{
                type: Input
            }], options: [{
                type: Input
            }], selected: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], selectItem: [{
                type: Output
            }], textfieldEl: [{
                type: ViewChild,
                args: ['textfieldEl', { static: false, read: ElementRef }]
            }], items: [{
                type: ViewChildren,
                args: [OptionDirective, { read: OptionDirective }]
            }], changeFocus: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

// tslint:disable-next-line: max-line-length
class AiHubSelectLabelDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AiHubSelectLabelDirective, selector: "[aihubSelectLabel]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aihubSelectLabel]'
                }]
        }] });
class AiHubSelectComponent {
    formBuilder;
    overlayPosition;
    overlayWidth = OverlayWidthStrategy.HOST;
    selectControl;
    valueChanged = new EventEmitter();
    dropdownTrigger;
    dropdown;
    /**
     * available options
     *
     */
    set options(options) {
        this.items = options.map((option) => typeof option === 'string' ? { label: option, value: option } : option);
    }
    items;
    /**
     * field label
     *
     */
    label = '';
    /**
     * current valu of the select box
     *
     */
    value = '';
    /**
     * optional enable free text field
     *
     */
    freetext = false;
    /**
     * optional maxlength
     *
     */
    maxlength;
    /**
     * Placeholder
     */
    placeholder = '';
    hasCustomLabel = false;
    customLabel;
    stateChangeSubscription;
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
    }
    /**
     * check we have inserted a custom label and render this one, otherwise it will
     * add a simple text passed by label
     *
     */
    ngAfterContentInit() {
        this.hasCustomLabel = this.customLabel instanceof AiHubSelectLabelDirective;
    }
    ngOnDestroy() {
        this.valueChanged.complete();
        this.valueChanged = null;
        this.stateChangeSubscription.unsubscribe();
        this.stateChangeSubscription = null;
    }
    ngOnInit() {
        this.overlayPosition = [{
                originY: 'top',
                originX: 'start',
                offsetY: 10,
                overlayY: 'top',
                overlayX: 'start'
            }];
        this.selectControl = this.formBuilder.control(this.resolveActiveOption()?.label ?? this.value);
        this.stateChangeSubscription = this.dropdownTrigger.stateChange
            .subscribe((state) => {
            if (state === 1) {
                this.dropdown.enable();
            }
        });
    }
    /**
     * option get selected
     */
    onSelect(option) {
        this.selectControl.setValue(option.label);
        this.dropdownTrigger.close();
        this.value = option.value;
        this.valueChanged.emit(this.value);
    }
    /**
     * resolve active option
     *
     */
    resolveActiveOption() {
        return this.items.find((option) => option.value === this.value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectComponent, deps: [{ token: i1$1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AiHubSelectComponent, selector: "aihub-select", inputs: { options: "options", label: "label", value: "value", freetext: "freetext", maxlength: "maxlength", placeholder: "placeholder" }, outputs: { valueChanged: "valueChanged" }, queries: [{ propertyName: "customLabel", first: true, predicate: AiHubSelectLabelDirective, descendants: true, read: AiHubSelectLabelDirective }], viewQueries: [{ propertyName: "dropdownTrigger", first: true, predicate: AihubDropdownTriggerDirective, descendants: true, read: AihubDropdownTriggerDirective, static: true }, { propertyName: "dropdown", first: true, predicate: OptionListComponent, descendants: true, read: OptionListComponent, static: true }], ngImport: i0, template: "<label class=\"aihub-select-field\">\n    <small *ngIf=\"!hasCustomLabel\">{{label}}</small>\n    <!--render custom label if passed -->\n    <ng-content select=\"[aihubSelectLabel]\"></ng-content>\n    <input type=\"text\" [aihubDropdown]=\"optionList\" [formControl]=\"selectControl\" readonly [placeholder]=\"placeholder\">\n</label>\n\n<aihub-dropdown #optionList [position]=\"overlayPosition\" [widthStrategy]=\"overlayWidth\">\n    <aihub-select-option-list [freetext]=\"freetext\" [options]=\"items\" [selected]=\"value\" [maxlength]=\"maxlength\"\n        (selectItem)=\"onSelect($event)\">\n    </aihub-select-option-list>\n</aihub-dropdown>", styles: [":host .aihub-select-field{display:block;position:relative}:host .aihub-select-field input[type=text]{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;line-height:24px;font-family:OpenSansRegular}:host .aihub-select-field input[type=text]:focus{outline:none;border:1px solid #86bc25;box-shadow:0 5px 5px #86bc2552}:host .aihub-select-field:after{content:\"\";position:absolute;display:block;height:.45rem;width:.45rem;border:1px solid;border-color:transparent transparent black black;transform:rotate(-45deg);flex-shrink:1;flex-grow:0;align-self:center;margin-left:.5rem;right:.5rem;bottom:calc(26px - .225rem)}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.AiHubDropdownOverlayComponent, selector: "aihub-dropdown", inputs: ["className", "position", "widthStrategy"] }, { kind: "directive", type: i3.AihubDropdownTriggerDirective, selector: "[aihubDropdown]", inputs: ["aihubDropdown"], outputs: ["stateChange"] }, { kind: "directive", type: i1$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: OptionListComponent, selector: "aihub-select-option-list", inputs: ["freetext", "label", "options", "selected", "maxlength"], outputs: ["selectItem"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-select', template: "<label class=\"aihub-select-field\">\n    <small *ngIf=\"!hasCustomLabel\">{{label}}</small>\n    <!--render custom label if passed -->\n    <ng-content select=\"[aihubSelectLabel]\"></ng-content>\n    <input type=\"text\" [aihubDropdown]=\"optionList\" [formControl]=\"selectControl\" readonly [placeholder]=\"placeholder\">\n</label>\n\n<aihub-dropdown #optionList [position]=\"overlayPosition\" [widthStrategy]=\"overlayWidth\">\n    <aihub-select-option-list [freetext]=\"freetext\" [options]=\"items\" [selected]=\"value\" [maxlength]=\"maxlength\"\n        (selectItem)=\"onSelect($event)\">\n    </aihub-select-option-list>\n</aihub-dropdown>", styles: [":host .aihub-select-field{display:block;position:relative}:host .aihub-select-field input[type=text]{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6;line-height:24px;font-family:OpenSansRegular}:host .aihub-select-field input[type=text]:focus{outline:none;border:1px solid #86bc25;box-shadow:0 5px 5px #86bc2552}:host .aihub-select-field:after{content:\"\";position:absolute;display:block;height:.45rem;width:.45rem;border:1px solid;border-color:transparent transparent black black;transform:rotate(-45deg);flex-shrink:1;flex-grow:0;align-self:center;margin-left:.5rem;right:.5rem;bottom:calc(26px - .225rem)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }]; }, propDecorators: { valueChanged: [{
                type: Output
            }], dropdownTrigger: [{
                type: ViewChild,
                args: [AihubDropdownTriggerDirective, { static: true, read: AihubDropdownTriggerDirective }]
            }], dropdown: [{
                type: ViewChild,
                args: [OptionListComponent, { static: true, read: OptionListComponent }]
            }], options: [{
                type: Input
            }], label: [{
                type: Input
            }], value: [{
                type: Input
            }], freetext: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], customLabel: [{
                type: ContentChild,
                args: [AiHubSelectLabelDirective, { read: AiHubSelectLabelDirective }]
            }] } });

class AiHubSelectModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectModule, declarations: [AiHubSelectComponent,
            OptionListComponent,
            OptionComponent,
            OptionDirective,
            AiHubSelectLabelDirective], imports: [CommonModule,
            AihubDropdownModule,
            ReactiveFormsModule], exports: [AiHubSelectComponent,
            AiHubSelectLabelDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectModule, imports: [CommonModule,
            AihubDropdownModule,
            ReactiveFormsModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiHubSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AiHubSelectComponent,
                        OptionListComponent,
                        OptionComponent,
                        OptionDirective,
                        AiHubSelectLabelDirective
                    ],
                    imports: [
                        CommonModule,
                        AihubDropdownModule,
                        ReactiveFormsModule
                    ],
                    exports: [
                        AiHubSelectComponent,
                        AiHubSelectLabelDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AiHubSelectComponent, AiHubSelectLabelDirective, AiHubSelectModule };
